import Service, { type Registry as Services, service } from '@ember/service';
import { TOAST_TIMEOUT_LONG } from '@mylighthouse/prism-foundation';
import { task } from 'ember-concurrency';
import type {
  BenchmarkInsightCompetitorsCompetitor,
  BenchmarkInsightCompetitorsCompset,
  BenchmarkInsightCompetitorsDesiredCompset,
} from 'lumen/models';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';

export default class BenchmarkInsightCompetitorsDesiredCompsetService extends Service {
  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];

  desiredCompsetTask = task(async () => {
    try {
      const desiredCompsetRecord =
        await this.store.queryRecord<BenchmarkInsightCompetitorsDesiredCompset>(
          'benchmark-insight/competitors/desired-compset',
          {
            typeKey: 'benchmarkinsight/desired_compset',
            subscription_id: this.hotelAndMarketService.hotel.id,
          },
        );

      if (desiredCompsetRecord && desiredCompsetRecord.competitors.length) {
        desiredCompsetRecord.hasPrefilledDesiredCompetitors = true;
      }

      return desiredCompsetRecord;
    } catch (error) {
      this.errorTracking.captureException(error);
    }

    return null;
  });

  saveDesiredCompsetTask = task(
    async (showToast: boolean, successCallback: () => void): Promise<void> => {
      try {
        if (!this.desiredCompset) {
          throw new Error('No desired compset to save');
        }

        await this.desiredCompset.save();

        if (showToast) {
          this.toast.success(
            this.intl.t(
              'benchmark_insight.desired_compset.builder.toast.save.success',
            ),
            {
              timeout: TOAST_TIMEOUT_LONG,
              showIcon: true,
            },
          );
        }

        successCallback();
      } catch (error) {
        this.errorTracking.captureException(error);
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        this.alerting.showErrorAlert();
      }
    },
  );

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get desiredCompetitors() {
    if (!this.desiredCompset) {
      return [];
    }

    return this.desiredCompset.competitors;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get desiredCompset() {
    return this.desiredCompsetTask.last?.value ?? null;
  }

  get isLoading(): boolean {
    return this.desiredCompsetTask.isRunning ?? false;
  }

  get isSearchInputDisabled(): boolean {
    if (!this.desiredCompset) {
      return false;
    }

    return this.desiredCompset.hasReachedMaxCompetitors;
  }

  discardChanges(): void {
    if (!this.desiredCompset) {
      return;
    }

    // Delete any newly created desired competitors.
    this.desiredCompset.competitors.forEach((competitor) => {
      competitor.rollbackAttributes();
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  getUnusedDesiredCompetitors(compsets: BenchmarkInsightCompetitorsCompset[]) {
    // Filter out the desired competitors that are in the proposed compsets
    const uniqueProposedCompsetCompetitorIds = new Set();
    compsets.forEach((compset) => {
      compset.competitors
        .map((competitor) => competitor.id)
        .forEach((id) => {
          uniqueProposedCompsetCompetitorIds.add(id);
        });
    });

    return this.desiredCompetitors.filter(
      (desiredCompetitor) =>
        !uniqueProposedCompsetCompetitorIds.has(desiredCompetitor.id),
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  isSuggestedCompetitor(competitor: BenchmarkInsightCompetitorsCompetitor) {
    if (!this.desiredCompset) {
      return false;
    }

    const competitorId = competitor.id;

    // If the proposed competitor is not in the desired compset, it means we suggested the competitor.
    return !this.desiredCompset.competitors.some((c) => c.id === competitorId);
  }
}

declare module '@ember/service' {
  interface Registry {
    'benchmark-insight/competitors/desired-compset': BenchmarkInsightCompetitorsDesiredCompsetService;
  }
}
